import { useProjectSubmittedBefore2026 } from '@/hooks/useProjectSubmittedBefore2026';
import { Box, toast, useOpenClose } from '@palmetto/palmetto-components';
import { Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { AttestmentData, ProgramType, QuoteStatus } from 'types';

import { formatKWHNumber } from '../../helpers/kwhFormatter';
import { getValueChangeInfo } from '../../helpers/productionValueCheck';
import { useGetAccountQuery } from '../../services/accounts';
import { useGetCurrentContractQuery, useVoidContractMutation } from '../../services/contract';
import { useGetQuoteSettingsQuery, useGetQuotesQuery } from '../../services/quotes';
import { useCreateSystemDesignMutation, useGetCurrentDesignQuery } from '../../services/systemDesign';
import { CheckRequestFormData, ConfirmObject, ErrorObject, Origin } from '../../types/ProductionValueCheck';
import { ReviewPackageActions } from '../../types/ReviewPackage';
import { MainContainer } from '../MainContainer';

import { AttestmentModal } from './AttestmentModal';
import { ChangeOrderSuggestionBanner } from './ChangeOrderSuggestionBanner';
import { InputForm } from './Form';
import { HeaderElement } from './Header';

function getNextRoute(
  origin: Origin,
  account: any,
  action = ReviewPackageActions.create,
  useItcSystemDesign?: boolean,
) {
  let route: string = `/accounts/${account?.id}`;
  if ([Origin.INSTALL_PACKAGE, Origin.ACCOUNT_VIEW].includes(origin)) {
    if (account?.programType === ProgramType.doePr) {
      route = `/accounts/${account?.id}/installation-package/${action}`;
    } else if (useItcSystemDesign) {
      route = `/accounts/${account?.id}/itc-system-design`;
    } else {
      route = `/accounts/${account?.id}/installation-package/${action}`;
    }
  }
  if (origin === Origin.PV_SYSTEM_TAB) {
    route = `/accounts/${account?.id}/system-design`;
  }

  return route;
}

function moveForward(
  origin: Origin,
  account: any,
  navigate: any,
  didUpdate: boolean,
  action = ReviewPackageActions.create,
  useItcSystemDesign?: boolean,
) {
  const route: string = getNextRoute(origin, account, action, useItcSystemDesign);
  navigate(route, {
    state: {
      justUpdated1yEstimate: didUpdate,
      shouldRedirect: false,
    },
  });
}

const newSystemValuesSchema = (shouldValidateSystemSize: boolean) =>
  yup.object({
    systemFirstYearProductionKwh: yup
      .mixed()
      .test({
        test: function (value: unknown) {
          if (!value) return true;
          const numberValue = Number(value);
          const isValidNumber = /^[0-9.]{1,25}$/.test(String(value));
          if (isNaN(numberValue) || !isValidNumber) {
            return this.createError({ message: 'Production must be a valid number' });
          }
          if (numberValue < 0) {
            return this.createError({ message: 'Production must be a positive number' });
          }
          return true;
        },
      })
      .required('Required for production validation'),
    systemSizeKw: yup
      .mixed()
      .test({
        test: function (value: unknown) {
          if (!value || shouldValidateSystemSize) return true;
          const numberValue = Number(value);
          const isValidNumber = /^[0-9.]{1,25}$/.test(String(value));
          if (isNaN(numberValue) || !isValidNumber) {
            return this.createError({ message: 'System size must be a valid number' });
          }
          if (numberValue < 0) {
            return this.createError({ message: 'System size must be a positive number' });
          }
          return true;
        },
      })
      .required('Required for system size validation'),
  });

export function ProductionValueCheck() {
  const { id: accountId } = useParams<{ id: any }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { systemSizeChangeCalculator = false, easyChangeOrder = false } = useFlags();
  const wasSubmittedBefore2026 = useProjectSubmittedBefore2026(accountId);

  const { data: account } = useGetAccountQuery(accountId);
  const programType = account?.programType;
  const { data: currentDesignQueryData } = useGetCurrentDesignQuery(
    { id: accountId, programType },
    { skip: !programType },
  );
  const [createSystemDesign] = useCreateSystemDesignMutation();
  const { data: quotes } = useGetQuotesQuery({ id: accountId, programType }, { skip: !programType });
  const { data: quoteSettings } = useGetQuoteSettingsQuery(
    { programType },
    {
      skip: !programType,
    },
  );
  const { data: contract } = useGetCurrentContractQuery(accountId);

  const {
    isOpen: isAttestmentModalOpen,
    handleClose: handleCloseAttestmentModal,
    handleOpen: handleOpenAttestmentModal,
  } = useOpenClose();

  const [voidContract] = useVoidContractMutation();
  const [isVoiding, setIsVoiding] = useState(false);

  const origin = location?.state?.from || null;
  const action = location?.state?.action;
  const contractQuote = (quotes || []).find(
    (element: any) => element.id === contract?.quoteId && element.status === QuoteStatus.active,
  );
  const contractedYearOneProduction = contractQuote?.systemFirstYearProductionKwh || 0;
  const contractedSystemSize = contractQuote?.systemSizeKw || 0;
  const currentDesignProdKwH =
    currentDesignQueryData?.systemFirstYearProductionKwh || account?.systemDesign?.systemFirstYearProductionKwh || 0;
  const currentSystemSize = currentDesignQueryData?.systemSizeKw || account?.systemDesign?.systemSizeKw || 0;

  const [isChecking, setIsChecking] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [formData, setFormData] = useState<CheckRequestFormData>();
  const primaryApplicantName = account?.primaryApplicantName;
  const [errorObject, setErrorObject] = useState<ErrorObject>();
  const [confirmObject, setConfirmObject] = useState<ConfirmObject>();
  const [attestmentData, setAttestmentData] = useState<AttestmentData>();

  useEffect(() => {
    const nextRoute = getNextRoute(origin, account, action, !wasSubmittedBefore2026);
    if (account?.systemDesign?.attestment && origin !== Origin.INSTALL_PACKAGE && origin !== Origin.PV_SYSTEM_TAB)
      navigate(nextRoute);
  }, [accountId, navigate, account?.systemDesign?.attestment, origin, action, wasSubmittedBefore2026]);

  const validationSchema = useMemo(
    () => newSystemValuesSchema(systemSizeChangeCalculator),
    [systemSizeChangeCalculator],
  );

  async function handleAttestmentConfirmation(attestmentData: AttestmentData) {
    setAttestmentData(attestmentData);
    //This part controls the execution of the update as soon as the attestment happens.
    if (attestmentData && formData) {
      try {
        await requestUpdate(formData, attestmentData);
      } catch (requestUpdate_e) {
        console.error({ requestUpdate_e });
      } finally {
        handleCloseAttestmentModal();
      }
    }
  }

  async function requestUpdate(data: CheckRequestFormData, attestmentData?: AttestmentData) {
    let didUpdate = false;
    setIsUpdating(true);
    try {
      await createSystemDesign({
        systemDesign: {
          ...(currentDesignQueryData || account.systemDesign),
          systemFirstYearProductionKwh: Number(data.systemFirstYearProductionKwh),
          ...(systemSizeChangeCalculator ? { systemSizeKw: Number(data.systemSizeKw) } : {}),
          attestment: attestmentData,
          toleranceException: null,
        },
        accountId: account.id,
        programType: programType as ProgramType,
      }).unwrap();
      didUpdate = true;
      moveForward(origin, account, navigate, didUpdate, action, !wasSubmittedBefore2026);
    } catch (requestUpdateError) {
      const potentialError = requestUpdateError as any;
      if (
        quoteSettings &&
        `${potentialError.data.message || ''}`.match(
          'The New Total Year-1 Production Estimate value is not valid: must void the contract and the active quote first.',
        )
      ) {
        const productionInfo = getValueChangeInfo({
          newValue: Number(data.systemFirstYearProductionKwh),
          currentValue: currentDesignProdKwH,
          contractedValue: contractedYearOneProduction,
          settings: quoteSettings,
        });
        setErrorObject({
          production: {
            percentErrorDiff: productionInfo.percentDifference,
            errorKwH: productionInfo.difference,
          },
        });
        toast.error((requestUpdateError as any)?.data?.message);
      } else {
        const errorMessage = potentialError?.data?.message;
        toast.error(errorMessage || 'An error has occurred. Please try again later.');
      }
    } finally {
      setIsUpdating(false);
      setConfirmObject(undefined);
    }
  }

  async function requestCheck(data: CheckRequestFormData) {
    setFormData(data);
    setIsChecking(true);
    setErrorObject(undefined);
    if (
      +data.systemFirstYearProductionKwh === account.systemDesign.systemFirstYearProductionKwh &&
      +data.systemSizeKw === account.systemDesign.systemSizeKw &&
      account.systemDesign.attestment
    ) {
      moveForward(origin, account, navigate, false, action, !wasSubmittedBefore2026);
    }
    const hasContractedYearOneProductionValue = contractedYearOneProduction > 0;
    const hasContractedSystemSizeValue = contractedSystemSize > 0;
    const newProduction = Number(data.systemFirstYearProductionKwh);
    const newSystemSize = Number(data.systemSizeKw);
    const productionChangeInfo = getValueChangeInfo({
      newValue: newProduction,
      currentValue: currentDesignProdKwH,
      contractedValue: hasContractedYearOneProductionValue ? contractedYearOneProduction : null,
      settings: quoteSettings,
    });
    const systemSizeChangeInfo = systemSizeChangeCalculator
      ? getValueChangeInfo({
          newValue: newSystemSize,
          currentValue: currentSystemSize,
          contractedValue: hasContractedSystemSizeValue ? contractedSystemSize : null,
          settings: quoteSettings,
        })
      : {
          valid: true,
          difference: 0,
          percentDifference: 0,
          changed: false,
          baseValue: currentSystemSize,
        };
    const isYearOneProductionValueValidRange = productionChangeInfo.valid;
    const isSystemSizeValidRange = systemSizeChangeInfo.valid;
    const changed = productionChangeInfo.changed || (systemSizeChangeCalculator && systemSizeChangeInfo.changed);
    const comesFromInstallPackage = origin === Origin.INSTALL_PACKAGE;
    const comesFromPvSystemTab = origin === Origin.PV_SYSTEM_TAB;
    const baseConfirmObj: ConfirmObject = {
      step: 0,
      production: {
        diffKwH: productionChangeInfo.difference,
        percentDiff: productionChangeInfo.percentDifference,
      },
      systemSize: {
        diffKw: systemSizeChangeInfo.difference,
        percentDiff: systemSizeChangeInfo.percentDifference,
      },
    };

    setIsChecking(false);

    if (!quoteSettings) {
      navigate(getNextRoute(origin, account, action, !wasSubmittedBefore2026));
      return;
    }
    if (
      comesFromInstallPackage &&
      confirmObject &&
      !attestmentData &&
      isYearOneProductionValueValidRange &&
      isSystemSizeValidRange
    ) {
      switch (confirmObject.step || 0) {
        case 2:
          return handleOpenAttestmentModal();
        default:
          setConfirmObject({
            ...baseConfirmObj,
            step: 2,
          });
          return handleOpenAttestmentModal();
      }
    }

    if (!isYearOneProductionValueValidRange || !isSystemSizeValidRange) {
      const newErrorObj: ErrorObject = {};
      if (!isYearOneProductionValueValidRange) {
        newErrorObj.production = {
          percentErrorDiff: productionChangeInfo.percentDifference,
          errorKwH: productionChangeInfo.difference,
        };
      }
      if (!isSystemSizeValidRange && systemSizeChangeCalculator) {
        newErrorObj.systemSize = {
          percentErrorDiff: systemSizeChangeInfo.percentDifference,
          errorKw: systemSizeChangeInfo.difference,
        };
      }
      setErrorObject(newErrorObj);
      setConfirmObject({
        ...baseConfirmObj,
        step: confirmObject?.step ?? 0,
      });
      return;
    }

    if (isYearOneProductionValueValidRange && isSystemSizeValidRange && changed) {
      if (confirmObject) {
        if (comesFromPvSystemTab) return requestUpdate(data);
        handleOpenAttestmentModal();
      } else {
        setConfirmObject({
          ...baseConfirmObj,
          step: 1,
        });
      }
      return;
    }

    if (!confirmObject) {
      setConfirmObject({
        ...baseConfirmObj,
        step: 2,
      });
      return;
    } else {
      if (comesFromPvSystemTab) return requestUpdate(data);
      return handleOpenAttestmentModal();
    }
  }

  const handleOnInput = function () {
    setConfirmObject(undefined);
    setAttestmentData(undefined);
  };

  async function requestVoid() {
    if (!contract?.id) return;
    setIsVoiding(true);
    try {
      await voidContract({
        accountId: accountId,
        contractId: contract.id,
      }).unwrap();

      navigate(`/accounts/${accountId}`, {
        state: {
          justVoidedContract: true,
          justUpdated1yEstimate: true,
        },
      });
    } catch (error) {
      console.error({ error });
      if (error) {
        toast.error('An error has occurred trying to void the contract. Please try again.');
      } else {
        toast.error('An error has occurred. Please try again later.');
      }
    }
    setIsVoiding(false);
  }

  const loadingState = {
    isChecking,
    isVoiding,
    isUpdating,
  };

  const attestmentProductionLabel = `I attest that the Installed Total Year-1 Production Estimate of ${formatKWHNumber(
    parseFloat(formData?.systemFirstYearProductionKwh || '0'),
  )} kWh is based on the actual installed PV system design.`;
  const attestmentSystemSizeValue = Number(formData?.systemSizeKw ?? currentSystemSize ?? 0);
  const attestmentSystemSizeLabel = `I attest that the Current DC System Size is ${attestmentSystemSizeValue.toLocaleString(
    'en-US',
    { maximumFractionDigits: 2 },
  )} kW, based on actual solar modules installed.`;
  const attestmentLabels = systemSizeChangeCalculator
    ? [attestmentProductionLabel, attestmentSystemSizeLabel]
    : [attestmentProductionLabel];

  return (
    <>
      <Helmet>
        <title>Check Production Value</title>
      </Helmet>
      <HeaderElement
        loadingState={loadingState}
        origin={origin}
        accountName={primaryApplicantName}
        accountId={accountId}
        errorObject={errorObject}
        requestVoid={requestVoid}
        easyChangeOrder={easyChangeOrder}
        formData={formData}
      />
      <MainContainer>
        <Box>
          <Formik
            initialValues={{
              systemFirstYearProductionKwh: currentDesignProdKwH,
              systemSizeKw: currentSystemSize,
            }}
            validationSchema={validationSchema}
            onSubmit={requestCheck}
            enableReinitialize={true}
            validateOnChange={true}
          >
            <>
              <ChangeOrderSuggestionBanner accountId={accountId} contractedSystemSize={contractedSystemSize} />
              <InputForm
                confirmObject={confirmObject}
                errorObject={errorObject}
                loadingState={loadingState}
                activeQuote={contractQuote}
                origin={origin}
                onChange={handleOnInput}
              />
            </>
          </Formik>
        </Box>
        <AttestmentModal
          isOpen={isAttestmentModalOpen}
          onDismiss={handleCloseAttestmentModal}
          onAttestmentConfirmed={handleAttestmentConfirmation}
          labels={attestmentLabels}
          title={'System Change Acknowledgement'}
          isLoading={isUpdating}
        />
      </MainContainer>
    </>
  );
}
